import { useEffect, useRef } from "react";

import { createCalendar } from "@internationalized/date";
import { useDateField, useDateSegment, useLocale } from "react-aria";
import {
  type DateFieldProps as AriaDateFieldProps,
  type DateSegmentProps as AriaDateSegmentProps,
  type DateValue,
  Input,
} from "react-aria-components";
import {
  type DateFieldState,
  type DateSegment as AriaDateSegment,
  useDateFieldState,
} from "react-stately";

import { cn } from "#utils";

type TimeSegmentType = "hour" | "minute" | "second" | "dayPeriod";

interface DateFieldProps extends AriaDateFieldProps<DateValue> {
  focusInputContainer: (focus: boolean) => void;
  /**
   * Pre-fills the (optional) time portion so a value can complete by typing only
   * a date — used by the date range picker: "start" maps to 00:00:00 and "end" to
   * 23:59:59. The time remains editable afterwards. When omitted, only the hidden
   * seconds segment is initialised.
   */
  defaultTimeOfDay?: "start" | "end";
}

export function DateField({ focusInputContainer, defaultTimeOfDay, ...props }: DateFieldProps) {
  const { locale } = useLocale();
  const state = useDateFieldState({
    ...props,
    locale,
    createCalendar,
  });

  const ref = useRef(null);
  const { fieldProps } = useDateField(props, state, ref);

  // Check if the date field is using a 12hr cycle time
  const is12HrCycle = state.segments.some((seg) => seg.type === "dayPeriod");

  // Segment values to pre-fill. The seconds segment is hidden from the UI (see
  // DateSegment), so a "second"-granularity value can never complete unless we
  // seed it. For range fields we additionally default the rest of the time so a
  // user can complete a value (and fire onRangeChange) by typing only a date.
  // dayPeriod must come before hour: setting hour otherwise auto-derives the day
  // period from the placeholder (see IncompleteDate.set).
  const timeDefaults: [TimeSegmentType, number][] = (() => {
    if (state.granularity !== "second") {
      return [];
    }
    if (defaultTimeOfDay === "start") {
      return [
        ...(is12HrCycle ? [["dayPeriod", 0] as [TimeSegmentType, number]] : []),
        ["hour", is12HrCycle ? 12 : 0],
        ["minute", 0],
        ["second", 0],
      ];
    }
    if (defaultTimeOfDay === "end") {
      return [
        ...(is12HrCycle ? [["dayPeriod", 1] as [TimeSegmentType, number]] : []),
        ["hour", is12HrCycle ? 11 : 23],
        ["minute", 59],
        ["second", 59],
      ];
    }
    return [["second", 0]];
  })();

  // Apply the defaults one segment per render. The segment setter is immutable
  // (IncompleteDate.set returns a copy), so multiple setSegment calls in a single
  // tick would all branch off the same stale value and only the last would stick.
  // We therefore set only the first still-placeholder default each render and let
  // the next render handle the next one. This is intentionally idempotent — it
  // keys off the current placeholder state rather than a counter — so React
  // StrictMode's double-invocation in development can't skip a segment. We never
  // touch a segment the user has already filled.
  //
  // The effect is self-rearming: it has no permanent "done" latch, so if a
  // controlled `value` is later reset to null (segments revert to placeholders),
  // the defaults — including the hidden, un-typeable seconds segment — are seeded
  // again. Without this, a value could never complete after a reset and
  // onRangeChange would never fire again until remount.
  const initAttempts = useRef(0);
  useEffect(() => {
    const pending = timeDefaults.find(
      ([type]) => state.segments.find((seg) => seg.type === type)?.isPlaceholder,
    );
    // Nothing left to fill — reset the attempt counter so a later reset can re-seed.
    if (!pending) {
      initAttempts.current = 0;
      return;
    }
    // Safety net if a segment refuses to settle (avoids an unbounded render loop).
    if (initAttempts.current > timeDefaults.length * 3) {
      return;
    }
    initAttempts.current += 1;
    state.setSegment(pending[0], pending[1]);
  });

  // Find the last separator index to hide the last ':' in 24hr mode
  // Separators are defined as type: literal segments and contain ':'
  let lastSeparatorIndex = -1;
  if (!is12HrCycle) {
    for (let i = state.segments.length - 1; i >= 0; i--) {
      if (state.segments[i]?.type === "literal" && state.segments[i]?.text === ":") {
        lastSeparatorIndex = i;
        break;
      }
    }
  }

  return (
    <div
      {...fieldProps}
      className={cn(
        "flex items-center justify-between gap-0",
        state.isInvalid ? "!text-text-negative" : "",
      )}
      ref={ref}
    >
      {state.segments.map((segment, i) =>
        i === lastSeparatorIndex ? null : (
          <DateSegment
            focusInputContainer={focusInputContainer}
            key={i}
            segment={segment}
            state={state}
          />
        ),
      )}
    </div>
  );
}

interface DateSegmentProps extends AriaDateSegmentProps {
  segment: AriaDateSegment;
  state: DateFieldState;
  focusInputContainer: (focus: boolean) => void;
  className?: string;
}

function DateSegment({ segment, state, className, focusInputContainer }: DateSegmentProps) {
  const ref = useRef(null);
  const { segmentProps } = useDateSegment(segment, state, ref);

  const isSeparator = !["day", "month", "year", "hour", "minute", "dayPeriod"].includes(
    segment.type,
  );

  if (segment.type === "second") {
    return null;
  }

  return !isSeparator ? (
    <Input
      {...segmentProps}
      aria-disabled={isSeparator}
      aria-label={`date-segment-${segment.text}`}
      className={cn(
        "h-5 border-none bg-transparent text-center text-label-medium text-text-primary !caret-text-primary outline-none",
        "font-mono disabled:text-text-disabled",
        state.isReadOnly ? "!text-text-primary" : "",
        segment.type === "year" ? "w-9" : "w-5",
        segment.isPlaceholder ? "uppercase text-text-secondary" : "",
        className,
      )}
      disabled={isSeparator || state.isDisabled}
      onBlur={(e) => {
        segmentProps.onBlur?.(e);
        focusInputContainer(false);
      }}
      onChange={(e) => {
        const value = e.target.value;

        // Non-numeric segments (AM/PM) can't be coerced with Number() — doing so
        // produces NaN, so the segment never updates and a time-granularity value
        // can never complete (which means onRangeChange never fires). Map the typed
        // character to a day period the same way react-aria's useDateSegment does:
        // 0 = AM, 1 = PM.
        if (segment.type === "dayPeriod") {
          const char = value.trim().charAt(0).toLowerCase();
          if (char === "a") {
            state.setSegment("dayPeriod", 0);
          } else if (char === "p") {
            state.setSegment("dayPeriod", 1);
          }
          return;
        }

        const parsed = Number(value);
        if (!Number.isNaN(parsed)) {
          state.setSegment(segment.type, parsed);
        }
      }}
      onFocus={(e) => {
        e.target.select();
        focusInputContainer(true);
      }}
      ref={ref}
      value={segment.text}
    />
  ) : (
    <span
      className={cn(
        "flex h-5 items-center justify-center font-light text-text-secondary",
        state.isDisabled ? "text-text-disabled opacity-70" : "",
        state.isReadOnly ? "text-text-primary" : "",
      )}
    >
      {segment.text}
    </span>
  );
}
